import { create } from 'zustand';

/**
 * Organisations the signed-in user belongs to, each with their role in it.
 *
 * Mirrors `teamsStore.js` — same shape, same stable module-level setters — so
 * the two load on the same lifecycle rather than each component fetching for
 * itself.
 *
 * `loaded` exists to distinguish "no organisations" from "not fetched yet".
 * Without it, every surface that hides itself for an org-less user would flash
 * hidden on first paint and then appear, which reads as a bug.
 */
const initialState = {
  organizations: [],
  loaded: false,
  // Teams per organisation, kept between mounts so reopening the page paints
  // from what is already known instead of an empty list. Refreshed in the
  // background on every visit — stale-while-revalidate, the same feel as
  // Teams: what you saw last time appears at once and corrects itself.
  teamsByOrg: {},
};

const make = (set, key) => (value) =>
  set((state) => ({ [key]: typeof value === 'function' ? value(state[key]) : value }));

export const useOrganizationsStore = create((set) => ({
  ...initialState,
  setOrganizations: make(set, 'organizations'),
  setLoaded: make(set, 'loaded'),
  setTeamsByOrg: make(set, 'teamsByOrg'),
  // Merged, not replaced: a refresh of one organisation must not blank the
  // others, which is what a whole-map write would do while the rest are still
  // in flight.
  mergeTeamsForOrg: (orgId, teams) =>
    set((state) => ({ teamsByOrg: { ...state.teamsByOrg, [String(orgId)]: teams } })),
  reset: () => set({ ...initialState }),
}));

export const setOrganizations = (v) => useOrganizationsStore.getState().setOrganizations(v);
export const setOrganizationsLoaded = (v) => useOrganizationsStore.getState().setLoaded(v);
export const mergeTeamsForOrg = (orgId, teams) =>
  useOrganizationsStore.getState().mergeTeamsForOrg(orgId, teams);

/**
 * The user's role in one organisation, or null.
 *
 * Returns null for an organisation the user is not in AND while the list is
 * still loading — a caller that treated "loading" as "no rights" would flicker
 * controls, and one that treated it as "has rights" would flash controls the
 * user may not be allowed to use. Callers that care read `loaded` too.
 */
export function roleInOrganization(organizationId) {
  if (organizationId == null) return null;
  const found = useOrganizationsStore
    .getState()
    .organizations.find((o) => String(o.id) === String(organizationId));
  return found?.role || null;
}

/**
 * Should this user see the organisations LEVEL at all?
 *
 * Enterprise is the tier organisations are sold with, so it is the gate — but
 * membership counts too, and that second half matters: a colleague added to an
 * enterprise customer's organisation is usually on their own free plan, and
 * gating on the plan alone would hide the organisation they are actually in.
 * Nobody can be a member without an admin having added them, so this cannot be
 * self-granted.
 *
 * Everyone else goes straight to Teams and never learns the level exists —
 * which is the point: a solo user should not have to understand organisations
 * to find their team.
 */
export function canSeeOrganizations(plan, organizations) {
  if (plan === 'enterprise') return true;
  return (organizations || []).length > 0;
}

/** Owner or admin of at least one organisation — the gate for showing the
 *  Organisations surface at all. */
export function administersAnyOrganization() {
  return useOrganizationsStore
    .getState()
    .organizations.some((o) => o.role === 'owner' || o.role === 'admin');
}
